import { UseDoubleBarChartOptionsProps } from '@components/design/migrated/DoubleBarChart/hooks/useDoubleBarChart';
import classNames from 'classnames';
import { ReactElement } from 'react';
import ReactDOMServer from 'react-dom/server';

import { formatKpi, Kpi } from '@utils/Formatters';
import { GrowthTypes } from './Types';

import * as styles from './PerformanceSegmentChart.module.scss';
import { CategoryLeaderboardPg } from '@models/CategoryLeaderboard';
import { getFormattedChangeValue, getSeries, getValueAndChangeValues, labelFormatter } from '@utils/TableUtils';
import { displayOption } from '../utilities/CommonOptions';
import { displayChangeType } from '../utilities/ChartUtils';
import { FactRevenue } from '@utils/pageConfig/Types';

interface FormatterProps {
  kpiKey: any;
  kpiLabel: string | ReactElement | Element;
  selectedGrowthType: GrowthTypes;
  categoryLeaderboardPg: CategoryLeaderboardPg[];
  factRevenue: FactRevenue;
}

export const isKpiPercentage = (kpiKey: any) => {
  return [Kpi.yourRevenueShare, Kpi.yourVolumeShare, Kpi.yourVolumeMix, Kpi.yourRevenueMix].includes(kpiKey);
};

export const getChartOptions = ({
  kpiKey,
  selectedGrowthType,
  categoryLeaderboardPg,
  kpiLabel,
  factRevenue,
}: FormatterProps): UseDoubleBarChartOptionsProps => {
  const leftFormatter = (value: number): string =>
    formatKpi(kpiKey, value, { currencySymbol: factRevenue.currencySymbol });

  const rightFormatter = (value: number): string =>
    formatKpi(kpiKey, value, {
      currencySymbol: factRevenue.currencySymbol,
      as: displayOption(kpiKey, selectedGrowthType),
    });

  const series = getSeries(categoryLeaderboardPg, kpiKey, selectedGrowthType, isKpiPercentage);

  const rowData = categoryLeaderboardPg.map((productGroup: CategoryLeaderboardPg) => {
    return {
      ...productGroup,
      label: `<div style=" font-weight: bold; max-height: 60px; min-width: 125px; white-space: normal;">${labelFormatter(
        productGroup.segmentOrPg,
        30,
      )}</div>
    `,
      rowHeight: 52,
    };
  });
  const tooltipFormatter = (row: any = {}): string => {
    const valueAndChangeValue = getValueAndChangeValues(kpiKey, selectedGrowthType, isKpiPercentage, row);
    const changeValue: number = valueAndChangeValue.changeValue;
    const value: number = valueAndChangeValue.value;

    const formattedChangeValue = getFormattedChangeValue(
      kpiKey,
      changeValue,
      selectedGrowthType,
      factRevenue.currencySymbol,
    );

    return ReactDOMServer.renderToString(
      <div className={styles.SegmentationBarChart__Tooltip} data-testid={'chart-tooltip'}>
        <div className={styles['PerformanceBarChart__Tooltip__RowHeading']}>
          <div>
            <div className={styles['PerformanceBarChart__Tooltip__RowHeading__Heading']}>
              {row?.productGroup ?? row?.segmentOrPg}
            </div>
          </div>
        </div>
        <div>
          <div className={styles['PerformanceBarChart__Tooltip__Row']}>
            <p className={styles['PerformanceBarChart__Tooltip__Label']}>{kpiLabel}</p>
            <p className={styles['PerformanceBarChart__Tooltip__Value']}>
              {formatKpi(kpiKey, value, { currencySymbol: factRevenue.currencySymbol })}
            </p>
          </div>
          <div className={styles['PerformanceBarChart__Tooltip__Row']}>
            <p className={styles['PerformanceBarChart__Tooltip__Label']}>
              {kpiLabel} Change {displayChangeType(selectedGrowthType, kpiKey)}
            </p>
            <p
              className={classNames(
                styles['PerformanceBarChart__Tooltip__Value'],
                { [styles['PerformanceBarChart__Tooltip__Value--positive']]: changeValue > 0 },
                { [styles['PerformanceBarChart__Tooltip__Value--negative']]: changeValue < 0 },
              )}
            >
              {formattedChangeValue}
            </p>
          </div>
        </div>
      </div>,
    );
  };

  return {
    leftTitle: 'Absolute',
    rightTitle: 'Change',
    leftFormatter,
    rightFormatter,
    tooltipFormatter,
    rows: rowData,
    series,
  };
};
